import { supabase } from '../lib/supabase';
import { reprocessMemory } from './memoryReprocessService';

/**
 * Memory Service
 * Service for managing memory data and operations
 */

// Memory categories with display info
export const MEMORY_CATEGORIES = {
  celebrations: {
    label: 'Celebrations',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: '🎉'
  },
  desires: {
    label: 'Goals',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: '💭'
  },
  problems: {
    label: 'Problems',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: '⚠️'
  },
  action_plans: {
    label: 'Actions',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: '📋'
  },
  regrets: {
    label: 'Regrets',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: '😔'
  },
  personal: {
    label: 'Personal',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: '👤'
  },
  obligations: {
    label: 'Obligations',
    color: 'bg-orange-100 text-orange-800 border-orange-200',
    icon: '📝'
  }
};

// Get user memories with advanced filtering
export const getUserMemories = async (userId, options = {}) => {
  const {
    search = null,
    category = null,
    dateFrom = null,
    dateTo = null,
    sortBy = 'created_at',
    sortOrder = 'desc',
    page = 1,
    pageSize = 20
  } = options;

  try {
    const offset = (page - 1) * pageSize;

    const { data, error } = await supabase
      .rpc('get_user_memories_filtered', {
        target_user_id: userId,
        search_term: search,
        category_filter: category,
        date_from: dateFrom,
        date_to: dateTo,
        sort_by: sortBy,
        sort_order: sortOrder,
        page_limit: pageSize,
        page_offset: offset
      });

    if (error) {
      throw new Error(`Failed to fetch memories: ${error.message}`);
    }

    // Get total count for pagination (with filters applied)
    let totalCount = 0;
    let allMemoriesCount = 0;
    let categoryCounts = {};
    
    try {
      // Build count query with same filters as main query
      let countQuery = supabase
        .from('memories')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Apply the same filters for count
      if (search) {
        countQuery = countQuery.or(`utterance.ilike.%${search}%,summary.ilike.%${search}%`);
      }
      if (category) {
        countQuery = countQuery.eq('category', category);
      }
      if (dateFrom) {
        countQuery = countQuery.gte('created_at', dateFrom);
      }
      if (dateTo) {
        countQuery = countQuery.lte('created_at', dateTo);
      }

      const { count, error: countError } = await countQuery;
      if (!countError) {
        totalCount = count || 0;
      }

      // Get unfiltered total count for "All" button
      const { count: allCount, error: allCountError } = await supabase
        .from('memories')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);
      
      if (!allCountError) {
        allMemoriesCount = allCount || 0;
      }

      // Get category counts for filter buttons (with search and date filters but not category filter)
      let categoryQuery = supabase
        .from('memories')
        .select('category', { count: 'exact' })
        .eq('user_id', userId);

      if (search) {
        categoryQuery = categoryQuery.or(`utterance.ilike.%${search}%,summary.ilike.%${search}%`);
      }
      if (dateFrom) {
        categoryQuery = categoryQuery.gte('created_at', dateFrom);
      }
      if (dateTo) {
        categoryQuery = categoryQuery.lte('created_at', dateTo);
      }

      const { data: categoryData, error: categoryError } = await categoryQuery;
      if (!categoryError && categoryData) {
        // Count by category
        categoryCounts = categoryData.reduce((counts, memory) => {
          const cat = memory.category || 'unknown';
          counts[cat] = (counts[cat] || 0) + 1;
          return counts;
        }, {});
      }
    } catch (err) {
      console.warn('Error getting counts:', err);
    }

    return {
      data: data || [],
      totalCount,
      allMemoriesCount,
      totalPages: Math.ceil(totalCount / pageSize),
      currentPage: page,
      pageSize,
      categoryCounts,
      error: null
    };

  } catch (error) {
    console.error('Error fetching user memories:', error);
    return {
      data: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: page,
      pageSize,
      error: error.message
    };
  }
};

// Get single memory details
export const getMemoryDetails = async (memoryId) => {
  try {
    const { data, error } = await supabase
      .from('memories')
      .select('*')
      .eq('id', memoryId)
      .single();

    if (error) {
      throw new Error(`Failed to fetch memory details: ${error.message}`);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching memory details:', error);
    return { data: null, error: error.message };
  }
};

// Create new memory
export const createMemory = async (memoryData) => {
  try {
    const { data, error } = await supabase
      .from('memories')
      .insert({
        user_id: memoryData.user_id,
        category: memoryData.category,
        utterance: memoryData.utterance,
        summary: memoryData.summary || null,
        entities: memoryData.entities || null,
        nlu: memoryData.nlu || null,
        source: memoryData.source || null
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create memory: ${error.message}`);
    }

    // Rebuild embedding + graph links in the background; the row is saved.
    return { data, error: null, reprocess: reprocessMemory(data.id) };
  } catch (error) {
    console.error('Error creating memory:', error);
    return { data: null, error: error.message, reprocess: Promise.resolve({ ok: false, error: error.message }) };
  }
};

// Update memory
export const updateMemory = async (memoryId, memoryData) => {
  try {
    const { data, error } = await supabase
      .from('memories')
      .update({
        category: memoryData.category,
        utterance: memoryData.utterance,
        summary: memoryData.summary || null,
        entities: memoryData.entities || null,
        nlu: memoryData.nlu || null
      })
      .eq('id', memoryId)
      .select()
      .maybeSingle(); // Use maybeSingle() to handle edge cases gracefully

    if (error) {
      throw new Error(`Failed to update memory: ${error.message}`);
    }

    // Rebuild embedding + graph links in the background; the edit is saved.
    return { data, error: null, reprocess: reprocessMemory(memoryId) };
  } catch (error) {
    console.error('Error updating memory:', error);
    return { data: null, error: error.message, reprocess: Promise.resolve({ ok: false, error: error.message }) };
  }
};

// Delete memory
export const deleteMemory = async (memoryId) => {
  try {
    const { error } = await supabase
      .rpc('delete_memory_cascade', { p_memory_id: memoryId });

    if (error) {
      throw new Error(`Failed to delete memory: ${error.message}`);
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting memory:', error);
    return { error: error.message };
  }
};

// Delete multiple memories
export const deleteMemories = async (memoryIds) => {
  try {
    const results = await Promise.all(
      memoryIds.map((id) => supabase.rpc('delete_memory_cascade', { p_memory_id: id }))
    );

    const failed = results.find((result) => result.error);
    if (failed) {
      throw new Error(`Failed to delete memories: ${failed.error.message}`);
    }

    return { error: null };
  } catch (error) {
    console.error('Error deleting memories:', error);
    return { error: error.message };
  }
};

// Get memory category info
export const getCategoryInfo = (category) => {
  return MEMORY_CATEGORIES[category] || {
    label: category,
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: '📝'
  };
};

// A memory that a later correction replaced. It stays in the list, flagged,
// but the assistant no longer recalls it.
export const getReplacedInfo = (memory) => {
  if (!memory?.superseded_by) return null;
  return {
    replacedById: memory.superseded_by,
    replacementText: memory.superseded_by_utterance || null,
  };
};

// Format memory date
export const formatMemoryDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
};

// Truncate utterance for display
export const truncateUtterance = (utterance, maxLength = 150) => {
  if (!utterance) return '';
  if (utterance.length <= maxLength) return utterance;
  return utterance.substring(0, maxLength).trim() + '...';
};

// Extract entities from memory
export const extractEntitiesFromMemory = (memory) => {
  const entities = [];
  
  // Extract from entities field
  if (memory.entities && typeof memory.entities === 'object') {
    Object.entries(memory.entities).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        entities.push(...value.map(v => ({ type: key, value: v })));
      } else if (value) {
        entities.push({ type: key, value: value });
      }
    });
  }
  
  // Extract from NLU field
  if (memory.nlu && typeof memory.nlu === 'object') {
    if (memory.nlu.entities && Array.isArray(memory.nlu.entities)) {
      entities.push(...memory.nlu.entities.map(e => ({
        type: e.entity || e.type || 'unknown',
        value: e.value || e.text || e.name
      })));
    }
  }
  
  return entities.filter(e => e.value && e.value.toString().trim().length > 0);
};

// Get memory sentiment (if available in NLU)
export const getMemorySentiment = (memory) => {
  if (memory.nlu && memory.nlu.sentiment) {
    const sentiment = memory.nlu.sentiment;
    
    if (typeof sentiment === 'string') {
      return {
        label: sentiment,
        score: null,
        color: getSentimentColor(sentiment)
      };
    } else if (typeof sentiment === 'object') {
      const label = sentiment.label || sentiment.polarity || 'neutral';
      const score = sentiment.score || sentiment.confidence || null;
      
      return {
        label,
        score,
        color: getSentimentColor(label)
      };
    }
  }
  
  return null;
};

// Get sentiment color
const getSentimentColor = (sentiment) => {
  const normalizedSentiment = sentiment.toLowerCase();
  
  if (normalizedSentiment.includes('positive') || normalizedSentiment.includes('happy') || normalizedSentiment.includes('joy')) {
    return 'text-green-600';
  } else if (normalizedSentiment.includes('negative') || normalizedSentiment.includes('sad') || normalizedSentiment.includes('anger')) {
    return 'text-red-600';
  } else if (normalizedSentiment.includes('neutral')) {
    return 'text-gray-600';
  }
  
  return 'text-blue-600';
};

// Export memories to CSV
export const exportMemoriesToCSV = (memories) => {
  if (!memories || memories.length === 0) {
    throw new Error('No memory data to export');
  }

  const headers = [
    'Memory ID',
    'Category',
    'Created Date',
    'Memory'
  ];

  const csvContent = [
    headers.join(','),
    ...memories.map(memory => [
      memory.memory_id,
      memory.category,
      formatMemoryDate(memory.created_at),
      memory.utterance || ''
    ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  return csvContent;
};

// Download memories CSV
export const downloadMemoriesCSV = (memories, filename = 'user_memories.csv') => {
  try {
    const csvContent = exportMemoriesToCSV(memories);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
};

// Search memories by text
export const searchMemories = (memories, searchTerm) => {
  if (!searchTerm || !memories) return memories;
  
  const term = searchTerm.toLowerCase();
  
  return memories.filter(memory => 
    memory.utterance?.toLowerCase().includes(term) ||
    memory.summary?.toLowerCase().includes(term) ||
    memory.category?.toLowerCase().includes(term) ||
    extractEntitiesFromMemory(memory).some(entity => 
      entity.value.toString().toLowerCase().includes(term)
    )
  );
};

// Filter memories by category
export const filterMemoriesByCategory = (memories, category) => {
  if (!category || category === 'all' || !memories) return memories;
  
  return memories.filter(memory => memory.category === category);
};

// Sort memories
export const sortMemories = (memories, sortBy, sortOrder = 'desc') => {
  if (!memories) return [];
  
  const sorted = [...memories].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'created_at':
        aValue = new Date(a.created_at);
        bValue = new Date(b.created_at);
        break;
      case 'category':
        aValue = a.category || '';
        bValue = b.category || '';
        break;
      case 'utterance':
        aValue = a.utterance || '';
        bValue = b.utterance || '';
        break;
      default:
        aValue = a.created_at;
        bValue = b.created_at;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  
  return sorted;
};